/**
 *  @class
 *  @function UniversalContent
 */

if (!customElements.get('universal-content')) {
  class UniversalContent extends HTMLElement {
    constructor() {
      super();
      this.scrollTriggerInstance = null;
      this.timeline = null;
    }

    connectedCallback() {
      this.initMarkers();
      this.initAccordion();
      this.initScrollAnimations();

      // Handle resize
      this.resizeHandler = this.handleResize.bind(this);
      window.addEventListener('resize', this.resizeHandler);
    }

    disconnectedCallback() {
      // Cleanup on removal
      if (this.scrollTriggerInstance) {
        this.scrollTriggerInstance.kill();
      }
      if (this.timeline) {
        this.timeline.kill();
      }
      window.removeEventListener('resize', this.resizeHandler);
    }

    handleResize() {
      // Debounce resize
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        if (this.scrollTriggerInstance) {
          this.scrollTriggerInstance.refresh();
        }
      }, 250);
    }

    initMarkers() {
      const bodies = this.querySelectorAll('.universal-content--body');

      bodies.forEach(body => {
        body.innerHTML = body.innerHTML.replace(/\|([^|]+)\|/g, '<span class="text-marker">$1</span>');
      });
    }

    initAccordion() {
      const accordionTriggers = this.querySelectorAll('.accordion-trigger');

      accordionTriggers.forEach(trigger => {
        trigger.addEventListener('click', () => {
          const item = trigger.closest('.accordion-item');
          const isOpen = item.classList.contains('is-open');
          const accordion = trigger.closest('.universal-content--accordion');

          accordion.querySelectorAll('.accordion-item.is-open').forEach(openItem => {
            if (openItem !== item) {
              openItem.classList.remove('is-open');
              openItem.querySelector('.accordion-trigger').setAttribute('aria-expanded', 'false');
            }
          });

          if (isOpen) {
            item.classList.remove('is-open');
            trigger.setAttribute('aria-expanded', 'false');
          } else {
            item.classList.add('is-open');
            trigger.setAttribute('aria-expanded', 'true');
          }
        });
      });
    }

    initScrollAnimations() {
      const hasGsap = typeof gsap !== 'undefined';
      const hasScrollTrigger = typeof ScrollTrigger !== 'undefined';

      if (!hasGsap || !hasScrollTrigger) {
        console.log('[UniversalContent] GSAP or ScrollTrigger not available');
        return;
      }

      // Only run on desktop
      if (window.innerWidth < 1024) return;

      gsap.registerPlugin(ScrollTrigger);

      const wrapper = this.querySelector('.universal-content--wrapper');
      const textColumn = this.querySelector('.universal-content--text-column');
      const rows = this.querySelectorAll('.universal-content--row');
      const images = this.querySelectorAll('.universal-content--image');
      const dots = this.querySelectorAll('.universal-content--dot');

      if (!wrapper || !textColumn || rows.length === 0 || images.length === 0) return;

      // If only one slide, no animation needed
      if (rows.length === 1) {
        gsap.set(rows[0], { autoAlpha: 1 });
        gsap.set(images[0], { autoAlpha: 1 });
        return;
      }

      const totalSlides = rows.length;
      const self = this;

      // Set initial state - first visible, rest hidden
      rows.forEach((row, i) => {
        gsap.set(row, { autoAlpha: i === 0 ? 1 : 0 });
      });
      images.forEach((img, i) => {
        gsap.set(img, { autoAlpha: i === 0 ? 1 : 0 });
      });

      // Function to update active dot
      const updateDots = (activeIndex) => {
        dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === activeIndex);
        });
      };

      // Track current slide and navigation state
      let currentSlide = 0;
      let isNavigating = false;

      // Function to show specific slide
      const showSlide = (index) => {
        rows.forEach((row, i) => {
          gsap.set(row, { autoAlpha: i === index ? 1 : 0 });
        });
        images.forEach((img, i) => {
          gsap.set(img, { autoAlpha: i === index ? 1 : 0 });
        });
        currentSlide = index;
        updateDots(index);
      };

      // Create ScrollTrigger for pinning and progress tracking
      this.scrollTriggerInstance = ScrollTrigger.create({
        trigger: wrapper,
        start: "center center",
        end: "+=" + (totalSlides * 20) + "%",
        pin: wrapper,
        pinSpacing: true,
        onUpdate: (st) => {
          // Skip updates during programmatic navigation
          if (isNavigating) return;

          // Calculate which slide should be active based on progress
          // Divide progress into equal segments for each slide
          const segmentSize = 1 / totalSlides;
          let newSlide = Math.floor(st.progress / segmentSize);

          // Clamp to valid range
          newSlide = Math.max(0, Math.min(newSlide, totalSlides - 1));

          // Handle edge case at 100% progress
          if (st.progress >= 0.99) {
            newSlide = totalSlides - 1;
          }

          if (newSlide !== currentSlide) {
            showSlide(newSlide);
          }
        }
      });

      // Make dots clickable to navigate to slides
      dots.forEach((dot, i) => {
        dot.addEventListener('click', () => {
          const st = this.scrollTriggerInstance;
          if (!st || isNavigating) return;

          // Set navigation flag to prevent onUpdate interference
          isNavigating = true;

          // Show the slide immediately
          showSlide(i);

          // Calculate target scroll position for this slide
          const segmentSize = 1 / totalSlides;
          const targetProgress = (i * segmentSize) + (segmentSize * 0.1);
          const scrollRange = st.end - st.start;
          const targetScroll = st.start + (scrollRange * targetProgress);

          // Use native smooth scroll
          window.scrollTo({
            top: targetScroll,
            behavior: 'smooth'
          });

          // Clear navigation flag after scroll completes
          setTimeout(() => {
            isNavigating = false;
          }, 800);
        });
      });
    }
  }
  customElements.define('universal-content', UniversalContent);
}
